import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import { useFormikContext } from "formik";
import { format } from "date-fns";
import { GenericTextField } from "../../../components/common/FormFields";
import { TenantFormValues } from "../validation/tenantFormValidation";
import { Tenant } from "../../../api/tenantsApi";

interface TenantFormContentProps {
  mode: "create" | "edit";
  isLoading?: boolean;
  tenant?: Tenant | null;
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "MMM dd, yyyy");
  } catch {
    return value;
  }
};

export const TenantFormContent: React.FC<TenantFormContentProps> = ({
  mode,
  isLoading = false,
  tenant = null,
}) => {
  const formik = useFormikContext<TenantFormValues>();
  const isEditing = mode === "edit";

  if (!formik) {
    return null;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Company Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <GenericTextField
                name="name"
                label="Company Name"
                required
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GenericTextField
                name="contactEmail"
                label="Contact Email"
                type="email"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GenericTextField
                name="contactPhone"
                label="Contact Phone"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GenericTextField
                name="safetyEmail"
                label="Safety Notification Email"
                type="email"
                disabled={isLoading}
              />
            </Grid>
            {isEditing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formik.values.isActive}
                      onChange={(e) => {
                        formik.setFieldValue("isActive", e.target.checked);
                      }}
                      disabled={isLoading}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      {formik.values.isActive ? "Active" : "Inactive"}
                    </Typography>
                  }
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {isEditing && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Subscription Information
            </Typography>
            {tenant?.subscriptionStatus ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Subscription
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={
                        tenant.isTrialing ||
                        tenant.subscriptionStatus === "trialing"
                          ? `${tenant.planName || tenant.planCode || "Plan"} · Trial`
                          : tenant.subscriptionStatus === "active"
                            ? `${tenant.planName || tenant.planCode || "Plan"} · Paid`
                            : `${tenant.planName || tenant.planCode || "Plan"} · ${tenant.subscriptionStatus.replace(/_/g, " ")}`
                      }
                      size="small"
                      color={
                        tenant.isTrialing ||
                        tenant.subscriptionStatus === "trialing"
                          ? "info"
                          : tenant.subscriptionStatus === "active"
                            ? "success"
                            : "default"
                      }
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Starts
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(tenant.subscriptionStartDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    {tenant.isTrialing ? "Trial Ends" : "Period Ends"}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(
                      tenant.isTrialing
                        ? tenant.trialEndDate
                        : tenant.subscriptionEndDate || tenant.currentPeriodEnd
                    )}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No subscription on file for this tenant.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
